/**
 * outputFormat 駆動の classifications テーブル (列名動的化レベル 3、docs/105 / old2 移植)。
 *
 * old2 `PostDetailModal` の AiAnalysisSection 内 classifications テーブル (L674-711) を
 * 共通コンポーネント化したもの。`buildClassificationColumns(schema)` が返す動的列で
 * thead/tbody を描画し、`classificationRowBgClass(schema, row)` で行背景を決める。
 *
 * - schema (= `AiOutputFormat.schema`) が未取得 / object-array を持たない場合は null を返すため、
 *   呼び出し側はそのとき従来の簡易 `<ul>` 表示に fallback する (回帰ゼロ)。
 * - bousai の `category_code`+`category_name` / `hzr_code`+`hzr_name`、walker の
 *   `gql_code`+`gql_name` は buildClassificationColumns が自動で 1 セルに集約する。
 * - assessment はポジ/ネガ配色バッジ、行背景も assessment で着色 (old2 と同挙動)。
 */
import type { AiOutputFormatSchema, PostDetailVisibility } from '@citizen-walk/shared-types';
import {
  buildClassificationColumns,
  classificationRowBgClass,
} from './classificationTableRenderer';

export interface ClassificationsTableProps {
  /** `AiOutputFormat.schema`。fields[] 駆動の動的列の唯一の真実。 */
  schema: AiOutputFormatSchema;
  /** AI 解析結果の classifications 配列 (各 mode / generic から取り出した素の Record 群)。 */
  classifications: Record<string, unknown>[];
  /** issue #24: 分類軸/評価ラベルの列除外に使う可視性。未指定は全列表示 (後方互換)。 */
  visibility?: PostDetailVisibility;
}

/**
 * schema から列を組み立て、classifications を動的列のテーブルで描画する。
 * 列が組めない (object-array 無し) ときは null を返す。
 */
export function ClassificationsTable({
  schema,
  classifications,
  visibility,
}: ClassificationsTableProps): React.ReactElement | null {
  const columns = buildClassificationColumns(schema, visibility);
  if (!columns || classifications.length === 0) return null;
  return (
    <div className="overflow-x-auto -mx-1" data-testid="classifications-table">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-100 text-slate-600">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className="text-left px-1.5 py-1 border border-slate-200"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {classifications.map((cls, i) => (
            <tr key={i} className={classificationRowBgClass(schema, cls)}>
              {columns.map((col) => (
                <td key={col.key} className="align-top px-1.5 py-1 border border-slate-200">
                  {col.render(cls, i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
